// Component for SideBar Details
import * as React from 'react';
import moment from 'moment';

import { Button, Dialog, DialogContent, DialogTitle, Grid, IconButton, Paper, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { theme } from '../../theme/theme';
import SetupErrorDetailsModal from './SetupErrorDetailsModal';

// JS doesn't have interfaces
//  use this commented out definition as a reference for the props that should be accepted for this component 
// export interface SideBarDetailsProps {
//   cloudSystemDetails : CloudSystem
// }

const SideBarDetails = (props) => {

    const [isRawStatusOpen, setRawStatusOpen] = React.useState(false);
    const [isSetupDetailsOpen, setSetupDetailsOpen] = React.useState(false);

    const handleOpenRawStatusClicked = () => {
        setRawStatusOpen(true);
    };

    const handleRawStatusClosed = () => {
        setRawStatusOpen(false);
    };

    const handleOpenSetupDetailsClicked = () => {
        setSetupDetailsOpen(true);
    };

    const handleSetupDetailsClosed = () => {
        setSetupDetailsOpen(false);
    };

    const createDetailsRow = (key, value, index) => {
        return (
            <Grid key={index} container item direction={'row'} wrap='nowrap'>
            <Grid item sx={{ flexShrink: 0 }}>
                <Typography sx={{ color: theme.palette.sidebarContent.labelText }}>
                {key}
                </Typography>
            </Grid>
            <Grid sx={{ marginLeft: 'auto' }}>
                <Typography sx={{ color: theme.palette.sidebarContent.valueText }} textAlign="right">
                {value !== null && value !== undefined && value !== "" ? value : "-- --"}
                </Typography>
            </Grid>
            </Grid>
        );
    }

    const createDetailsSection = (sectionTitle, keyValuePairs) => {
        return (
            <Grid container direction={'row'} alignItems='center' sx={{ borderTop: '1px solid #707070', mb: 2, pt: 2 }}>
            <Grid item>
                <Typography fontWeight={700} sx={{ color: theme.palette.primary.contrastText, mb: 1 }}>
                {sectionTitle}
                </Typography>
            </Grid>
            {keyValuePairs.map((keyValuePair, index) => {return createDetailsRow(keyValuePair[0], keyValuePair[1], index)})}
            </Grid>
        );
    }

    const createInternetStatusSection = (setupErrors) => {
        const createPortRow = (name, port, valid) => {
            return (
                <Grid container item direction={'row'} wrap="nowrap">
                    <Grid item xs={8}><Typography sx={{ color: theme.palette.sidebarContent.labelText}}>{name}</Typography></Grid>
                    <Grid item xs={2} justifyContent="center"><Typography textAlign="center" sx={{ color: theme.palette.sidebarContent.valueText}}>{port}</Typography></Grid>
                    <Grid item xs={2} justifyContent="center"><Typography textAlign="center">{valid ? <CheckCircleIcon color="success"/> : <ErrorIcon color="error"/>}</Typography></Grid>
                </Grid>
            )
        }

        if (!setupErrors?.timestamp) {
            // We don't even have a timestamp, nothing to show here
            return createDetailsSection('Internet Status', [['No data available', ' ']])
        }
        return (
            <Grid container direction={'row'} alignItems='center' sx={{ borderTop: '1px solid #707070', mb: 2, pt: 2 }}>
            <Grid container item>
                <Grid container item direction={'row'} wrap="nowrap" sx={{mb: 1}}>
                    <Grid item xs={8}><Typography fontWeight={700} sx={{ color: theme.palette.primary.contrastText }}>Internet Status</Typography></Grid>
                    <Grid item xs={2} justifyContent="center"><Typography textAlign="center" sx={{ color: theme.palette.sidebarContent.labelText }}>Port</Typography></Grid>
                    <Grid item xs={2} justifyContent="center"><Typography textAlign="center" sx={{ color: theme.palette.sidebarContent.labelText }}>Status</Typography></Grid>
                </Grid>

                {createPortRow('Internet', '--', setupErrors?.resultConnAddress === 'Pass')}
                {createPortRow('Cloud/VPN', '1194/udp', setupErrors?.resultCommVPN === 'Pass')}
                {createPortRow('Software Updates', '22/tcp', setupErrors?.resultCommSoftware === 'Pass')}
                {createPortRow('MQTT', '8883/tcp', setupErrors?.resultCommMQTT === 'Pass')}

                <Grid container item sx={{
                    border: `1px solid ${theme.palette.tertiary.main}`,
                    backgroundColor: '#1A354B',
                    borderRadius: '4px',
                    justifyContent: 'space-between',
                    p: 2, my: 2 }}>
                    <Typography sx={{ color: theme.palette.sidebarContent.labelText }}>
                        Timestamp
                    </Typography>
                    <Typography sx={{ color: theme.palette.sidebarContent.valueText }}>
                        {setupErrors?.timestamp ? moment(setupErrors?.timestamp).format('MM/DD/YY hh:mm:ss a'): '-- --'}
                    </Typography>
                </Grid>

                    <Button onClick={handleOpenSetupDetailsClicked} 
                        sx={{
                            flexGrow: 1,
                            color: theme.palette.tertiary.contrastText, 
                            backgroundColor: theme.palette.tertiary.main,
                        }}
                    >
                        View Details
                    </Button>
            </Grid>
            </Grid>
        );
    }

    // WAN Address
    const wanAddressText = props.cloudSystemDetails?.activeStatus?.wanAddress;
    // VPN Address
    const vpnAddressText = props.cloudSystemDetails?.activeStatus?.vpnAddress;
    // Location
    const locationText = props.cloudSystemDetails?.activeStatus?.location;
    // Software Version
    const softwareVersion = props.cloudSystemDetails?.activeStatus?.softwareVersion;
    // Vision Catalog
    const visionCatalog = props.cloudSystemDetails?.activeStatus?.visionCatalogId;
    // WiFiClients
    const wifiClients = props.cloudSystemDetails?.activeStatus?.wifiClientsCount;

    const drmDateVal = props.cloudSystemDetails?.activeStatus?.visionDRMDate;
    const drmDateDisplay = isNaN(drmDateVal) || isNaN(parseInt(drmDateVal)) ? drmDateVal : moment.unix(Number(drmDateVal)).format('MMMM Do, YYYY')

    const magazineDateVal = props.cloudSystemDetails?.activeStatus?.visionMagazineDate;
    const magazineDateDisplay = isNaN(magazineDateVal) || isNaN(parseInt(magazineDateVal)) ? magazineDateVal : moment.unix(Number(magazineDateVal)).format('MMMM Do, YYYY')

    // Software Update Status
    // This comes as an enumeration, turn it into a user-friendly string.
    // Depending on the state and how long it is has been, we might instead want to display 
    // a duration since the last update message.
    let lastSoftwareUpdateDiff = ''
    const lastSoftwareUpdateTime = props.cloudSystemDetails?.activeStatus?.lastSoftwareUpdateTime
    if (lastSoftwareUpdateTime) {
        const OVERRIDE_INTERVAL_DURATION = moment.duration(5, 'minutes')
        if (moment(lastSoftwareUpdateTime).isBefore(moment().subtract(OVERRIDE_INTERVAL_DURATION))) {
            lastSoftwareUpdateDiff = `Last Updated: ${moment().from(new Date(lastSoftwareUpdateTime), true)}`
        }
    }
    let softwareUpdateStatus = props.cloudSystemDetails?.activeStatus?.softwareUpdateStatus;
    switch (softwareUpdateStatus) {
        case 'CHECKING_SW_UPDATES':
            softwareUpdateStatus = 'Checking for software updates'
            break;
        case 'INSTALLING_SW_UPDATES':
            softwareUpdateStatus = 'Installing software updates'
            break;
        case 'FAILED_SW_UPDATE':
            softwareUpdateStatus = 'Update failed'
            break;
        case 'SUCCESSFUL_SW_UPDATE':
            softwareUpdateStatus = 'Software update complete'
            break;
        case 'CHECKING_CONFIG_UPDATES':
            softwareUpdateStatus = 'Checking for configuration updates'
            break;
        case 'APPLYING_CONFIG_UPDATES':
            softwareUpdateStatus = 'Applying configuration updates'
            break;
        case 'FAILED_CONFIG_UPDATE':
            softwareUpdateStatus = 'Reverting configuration update'
            break;
        case 'COMPLETE_REBOOTING':
            softwareUpdateStatus = lastSoftwareUpdateDiff ? lastSoftwareUpdateDiff : 'Update complete. Rebooting'
            break;
        case 'COMPLETE_NO_INSTALL':
            softwareUpdateStatus = lastSoftwareUpdateDiff ? lastSoftwareUpdateDiff : 'No updates found'
            break;
        default:
            // Leave whatever is in the data, since we can't interpret it
            break;
    }

    // -----------

    // Last Status
    const lastStatusUpTimeDiff = props.cloudSystemDetails?.activeStatus?.lastStatusUpdateTime ? moment().from(new Date(props.cloudSystemDetails.activeStatus.lastStatusUpdateTime), true) : "";
    // VPN Status
    const vpnStatus = props.cloudSystemDetails?.activeStatus?.vpnUpLastStatus ? "Up" : "Down";
    const vpnStatusUpTimeDiff = props.cloudSystemDetails?.activeStatus?.lastVPNStatusUpdateTime ? moment().from(new Date(props.cloudSystemDetails.activeStatus.lastVPNStatusUpdateTime), true) : "";
    const vpnStatusText = `${vpnStatus} ${vpnStatusUpTimeDiff}`;
    // AP Status
    const apStatus = ["Healthy"].includes(props.cloudSystemDetails?.activeStatus?.apHealth) ? "Up" : "Down";
    const apStatusUpTimeDiff = props.cloudSystemDetails?.activeStatus?.lastAPStatusUpdateTime ? moment().from(new Date(props.cloudSystemDetails.activeStatus.lastAPStatusUpdateTime), true) : "";
    const apStatusText = `${apStatus} ${apStatusUpTimeDiff}`;
    // Uptime
    const upTimeText = props.cloudSystemDetails?.activeStatus?.uptimeSeconds ? moment().from(moment().subtract(Number(props.cloudSystemDetails?.activeStatus?.uptimeSeconds), 'seconds'), true) : ""; // Convert to number, then parse to better duration format
    // Plane Syncs (48 hr)
    const planeSyncsText = props.cloudSystemDetails?.activeStatus?.lastTwoDaysPlaneSyncsCount;
    // Unique Planes
    const uniquePlanesText = props.cloudSystemDetails?.activeStatus?.uniquePlanesCount;

    // -----------

    const diskUsageText = props.cloudSystemDetails?.activeStatus?.diskUsage;
    const cpuUsageText = props.cloudSystemDetails?.activeStatus?.cpuUsage;

    return (
        <>
            <Paper
                elevation={0}
                sx={{
                    p: 2,
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: theme.palette.primary.main
                }}
            >
                {createDetailsSection('Device Details', [
                    ['WAN Address', wanAddressText], 
                    ['VPN Address', vpnAddressText], 
                    ['Location', locationText], 
                    ['Software Version', softwareVersion],
                    ['Software Update Status', softwareUpdateStatus],
                    ['Vision Catalog', visionCatalog],
                    ['Vision DRM', drmDateDisplay],
                    ['Vision Magazine', magazineDateDisplay],
                    ['WiFi Clients', wifiClients]
                ])}
                {createDetailsSection('Status & Run Data', [
                    ['Last Status', lastStatusUpTimeDiff], 
                    ['VPN Status', vpnStatusText], 
                    ['AP Status', apStatusText], 
                    ['Uptime', upTimeText],
                    ['Plane Syncs (48 hr)', planeSyncsText],
                    ['Unique Planes', uniquePlanesText]
                ])}
                {createInternetStatusSection(props.cloudSystemDetails?.setupErrors)}
                {createDetailsSection('Usage Metrics', [
                    ['Disk Usage', diskUsageText], 
                    ['CPU Usage', cpuUsageText]
                ])}
                <Button onClick={handleOpenRawStatusClicked} 
                    sx={{ 
                        color: theme.palette.tertiary.contrastText, 
                        backgroundColor: theme.palette.tertiary.main,
                    }}
                >
                    View Raw Status
                </Button>
            </Paper>
            <SetupErrorDetailsModal
                open={isSetupDetailsOpen}
                onClose={handleSetupDetailsClosed}
                setupErrors={props.cloudSystemDetails?.setupErrors}
            />
            <Dialog 
                open={isRawStatusOpen} 
                onClose={handleRawStatusClosed} 
                maxWidth={'md'}
                PaperProps={{
                    style: {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText
                    },
                }}
            >
                <DialogTitle disableTypography>
                    <Grid container direction='row' alignItems={'center'} justifyContent={'space-between'}>
                        <Grid item>
                            <Typography fontWeight={700}>
                                Raw Status
                            </Typography>
                        </Grid>
                        <Grid item>
                            <IconButton onClick={handleRawStatusClosed}>
                                <CloseIcon htmlColor='white' />
                            </IconButton>
                        </Grid>
                    </Grid>
                </DialogTitle>
                <DialogContent>
                    <Typography whiteSpace={'pre'}>
                        {props.cloudSystemDetails?.activeStatus?.lastStatusPayload && props.cloudSystemDetails?.activeStatus?.lastStatusPayload !== ""
                            ? props.cloudSystemDetails.activeStatus.lastStatusPayload 
                            : "No Status"}
                    </Typography>
                </DialogContent>
            </Dialog>
        </>
      );
};

export default SideBarDetails;